import React, { useState } from "react";

/**
 * PaymentDeclarationModal
 *
 * Image Location:
 * example/public/SBI.png
 * In Create React App with homepage 'digit-ui/', public assets are accessible at:
 * /digit-ui/SBI.png
 */

const PaymentDeclarationModal = ({ onAgree, onCancel }) => {
  const [agreed, setAgreed] = useState(false);

  // Determine the correct public path for the image based on runtime location
  const getInitialImageSrc = () => {
    if (typeof window !== "undefined") {
      if (window.location.pathname.includes("/digit-ui")) {
        return "/digit-ui/SBI.png";
      }
    }
    if (typeof process !== "undefined" && process?.env?.PUBLIC_URL) {
      return `${process.env.PUBLIC_URL}/SBI.png`;
    }
    return "/digit-ui/SBI.png";
  };

  const [imageSrc, setImageSrc] = useState(getInitialImageSrc);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageError = () => {
    // If /digit-ui/SBI.png fails, try /SBI.png and vice versa
    if (imageSrc.includes("/digit-ui/")) {
      setImageSrc("/SBI.png");
    } else if (imageSrc === "/SBI.png") {
      setImageSrc("/digit-ui/SBI.png");
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.dialog}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerTitle}>
            <span style={styles.headerIcon}>⚠</span>
            <span>Important Payment Information</span>
          </div>
          {onCancel && (
            <button
              onClick={onCancel}
              style={styles.closeButton}
              aria-label="Close"
              title="Close"
            >
              ✕
            </button>
          )}
        </div>

        {/* Scrollable Body */}
        <div style={styles.body}>
          <p style={styles.introText}>
            Before proceeding to the <strong>SBI Payment Gateway</strong>, please
            read the following instructions carefully.
          </p>

          {/* Highlight / Caution Note */}
          <div style={styles.blueNote}>
            <div style={styles.noteIcon}>⛔</div>
            <div style={styles.noteContent}>
              <div style={styles.noteTitle}>
                If you need to go back, do NOT click the browser Back button.
              </div>
              <div style={styles.noteBody}>
                Instead, click the{" "}
                <strong style={styles.cancelHighlight}>Cancel</strong> button
                available at the bottom-right corner of the SBI payment page.
              </div>
              <div style={styles.noteWarning}>
                Clicking the browser Back button may put your payment request on
                hold for <span style={styles.holdTime}>30 minutes</span>.
              </div>
            </div>
          </div>

          {/* Visual Instruction Guide / SBI Image */}
          <div style={styles.imageContainer}>
            <img
              src={imageSrc}
              alt="SBI Payment Gateway Instructions"
              style={{
                ...styles.image,
                display: imageLoaded ? "block" : "none",
              }}
              onLoad={() => setImageLoaded(true)}
              onError={handleImageError}
            />
            {!imageLoaded && (
              <div style={styles.imagePlaceholder}>
                <span>Loading instruction guide...</span>
              </div>
            )}
            <div style={styles.imageCaption}>
              📌 <strong>Visual Guide:</strong> Location of the Cancel button on
              the SBI Payment Gateway page. Do not use the browser's Back button.
            </div>
          </div>

          {/* Payment Modes Section */}
          <h3 style={styles.sectionTitle}>
            We provide 4 payment modes for making your payment:
          </h3>

          <div style={styles.paymentModesGrid}>
            <div style={styles.modeCard}>
              <span style={styles.modeIcon}>📄</span>
              <span style={styles.modeLabel}>NEFT</span>
            </div>
            <div style={styles.modeCard}>
              <span style={styles.modeIcon}>💳</span>
              <span style={styles.modeLabel}>Credit / Debit Card</span>
            </div>
            <div style={styles.modeCard}>
              <span style={styles.modeIcon}>📱</span>
              <span style={styles.modeLabel}>UPI</span>
            </div>
            <div style={styles.modeCard}>
              <span style={styles.modeIcon}>🌐</span>
              <span style={styles.modeLabel}>Net Banking</span>
            </div>
          </div>

          <p style={styles.modeDescription}>
            Please confirm your preferred payment mode carefully before
            proceeding. Once a payment mode is selected, make sure it is the mode
            you intend to use.
          </p>

          {/* NEFT Warning Box */}
          <div style={styles.warningBox}>
            <div style={styles.warningTitle}>
              ⚠ In case of NEFT challan creation:
            </div>
            <ul style={styles.warningList}>
              <li>
                The payment gateway supports <strong>NEFT only</strong> for this
                process.
              </li>
              <li>
                <strong>RTGS payments are NOT supported.</strong> Any payment
                made through RTGS will not be considered successful.
              </li>
              <li>
                Once you generate a NEFT challan, the payment mode{" "}
                <strong>cannot be changed for 7 days.</strong>
              </li>
              <li>
                In case of payment failure, you can reuse the same challan to
                make payment within <strong>7 days</strong> from the challan
                creation date.
              </li>
            </ul>
          </div>

          <p style={styles.footerNote}>
            Please carefully confirm your payment mode before proceeding to the
            SBI Payment Gateway.
          </p>
        </div>

        {/* Fixed Footer */}
        <div style={styles.footer}>
          <label style={styles.checkboxRow}>
            <input
              type="checkbox"
              style={styles.checkbox}
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <span style={styles.checkboxLabel}>
              I have read and agree to the payment instructions
            </span>
          </label>

          <div style={styles.buttonGroup}>
            <button
              type="button"
              style={styles.cancelButton}
              onClick={onCancel}
            >
              Cancel
            </button>

            <button
              type="button"
              disabled={!agreed}
              style={{
                ...styles.proceedButton,
                opacity: agreed ? 1 : 0.5,
                cursor: agreed ? "pointer" : "not-allowed",
              }}
              onClick={() => agreed && onAgree()}
            >
              Proceed
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0, 0, 0, 0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    zIndex: 9999,
    fontFamily: "Inter, Roboto, Arial, sans-serif",
    boxSizing: "border-box",
  },

  dialog: {
    width: "100%",
    maxWidth: "840px",
    maxHeight: "90vh",
    background: "#FFFFFF",
    borderRadius: "12px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 16px 48px rgba(0, 0, 0, 0.28)",
    boxSizing: "border-box",
  },

  header: {
    background: "#006C8E",
    color: "#FFFFFF",
    padding: "16px 22px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },

  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontWeight: 700,
    fontSize: "19px",
  },

  headerIcon: {
    fontSize: "22px",
    lineHeight: 1,
  },

  closeButton: {
    background: "transparent",
    border: "none",
    color: "#FFFFFF",
    fontSize: "20px",
    lineHeight: 1,
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0.85,
    transition: "opacity 0.2s",
  },

  body: {
    flex: 1,
    minHeight: 0,
    padding: "20px 24px",
    overflowY: "auto",
    color: "#333333",
    lineHeight: 1.6,
    fontSize: "14px",
    boxSizing: "border-box",
  },

  introText: {
    margin: "0 0 14px 0",
    fontSize: "15px",
    color: "#2B363B",
  },

  blueNote: {
    background: "#F0F7FF",
    border: "1px solid #90CAF9",
    borderLeft: "5px solid #1976D2",
    borderRadius: "8px",
    padding: "14px 16px",
    display: "flex",
    gap: "12px",
    alignItems: "flex-start",
    marginBottom: "18px",
  },

  noteIcon: {
    fontSize: "24px",
    lineHeight: "28px",
    flexShrink: 0,
  },

  noteContent: {
    flex: 1,
  },

  noteTitle: {
    fontWeight: 700,
    fontSize: "15px",
    color: "#0D47A1",
    marginBottom: "4px",
  },

  noteBody: {
    fontSize: "14px",
    color: "#333333",
    lineHeight: 1.5,
  },

  cancelHighlight: {
    color: "#0D47A1",
    fontWeight: 700,
  },

  noteWarning: {
    marginTop: "6px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#555555",
  },

  holdTime: {
    color: "#C62828",
    fontWeight: 700,
  },

  imageContainer: {
    margin: "16px 0 20px 0",
    border: "1px solid #D0E1FD",
    borderRadius: "10px",
    overflow: "hidden",
    background: "#F8FAFD",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "12px",
    boxSizing: "border-box",
  },

  image: {
    width: "100%",
    maxWidth: "760px",
    maxHeight: "360px",
    height: "auto",
    objectFit: "contain",
    display: "block",
    margin: "0 auto",
    borderRadius: "6px",
  },

  imagePlaceholder: {
    padding: "36px 16px",
    color: "#666666",
    fontSize: "14px",
    textAlign: "center",
    fontStyle: "italic",
  },

  imageCaption: {
    marginTop: "10px",
    fontSize: "12px",
    color: "#475569",
    textAlign: "center",
    lineHeight: 1.4,
  },

  sectionTitle: {
    color: "#006C8E",
    fontSize: "16px",
    fontWeight: 700,
    margin: "18px 0 10px 0",
  },

  paymentModesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: "10px",
    marginBottom: "12px",
  },

  modeCard: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "#F4F7F9",
    border: "1px solid #CFD8DC",
    borderRadius: "6px",
    padding: "8px 12px",
    fontWeight: 600,
    fontSize: "13px",
    color: "#2C3E50",
  },

  modeIcon: {
    fontSize: "16px",
  },

  modeLabel: {
    flex: 1,
  },

  modeDescription: {
    fontSize: "13px",
    color: "#555555",
    margin: "8px 0 16px 0",
    lineHeight: 1.5,
  },

  warningBox: {
    background: "#FFFBEB",
    border: "1px solid #FCD34D",
    borderLeft: "5px solid #F59E0B",
    borderRadius: "8px",
    padding: "14px 16px",
    marginTop: "14px",
  },

  warningTitle: {
    fontWeight: 700,
    color: "#92400E",
    fontSize: "14px",
    marginBottom: "8px",
  },

  warningList: {
    paddingLeft: "20px",
    margin: 0,
    lineHeight: 1.7,
    fontSize: "13px",
    color: "#78350F",
  },

  footerNote: {
    marginTop: "14px",
    marginBottom: "4px",
    fontWeight: 600,
    fontSize: "13px",
    color: "#475569",
  },

  footer: {
    borderTop: "1px solid #E2E8F0",
    padding: "14px 22px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "14px",
    background: "#FFFFFF",
    flexShrink: 0,
    boxSizing: "border-box",
  },

  checkboxRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontWeight: 600,
    cursor: "pointer",
    fontSize: "14px",
    userSelect: "none",
  },

  checkbox: {
    width: "18px",
    height: "18px",
    cursor: "pointer",
    accentColor: "#006C8E",
  },

  checkboxLabel: {
    color: "#1E293B",
  },

  buttonGroup: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },

  cancelButton: {
    padding: "9px 22px",
    borderRadius: "6px",
    border: "1.5px solid #006C8E",
    background: "#FFFFFF",
    color: "#006C8E",
    fontWeight: 700,
    fontSize: "14px",
    cursor: "pointer",
    transition: "background 0.2s, color 0.2s",
  },

  proceedButton: {
    padding: "9px 24px",
    borderRadius: "6px",
    border: "none",
    background: "#006C8E",
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: "14px",
    transition: "opacity 0.2s, background 0.2s",
  },
};

export default PaymentDeclarationModal;